// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.HighmapOptionsBuilder.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------
import ChartOptionsBuilder from '../Izenda.ChartOptionsBuilder';
import {
  get,
  getOr,
  head,
  has,
  size,
  each,
  isEmpty,
  find,
  isNil,
  omitBy,
  map,
  max,
  groupBy,
  escape,
  flow,
  toNumber,
  flatMap,
  some
} from 'lodash/fp';

import ChartDataParser from 'share/utils/Izenda.ChartDataParser';
import { MapUIConstant } from 'logic/data/Izenda.ReportPartMapConstants';
import { addPointActionLink, getMapTicksPositioner } from 'share/components/charting/highcharts/Izenda.HighchartUtils';
import { getConfigDataByRange } from 'share/utils/Izenda.ReportPartUtils';
import { exportClass } from 'share/Izenda.ServiceProvider';
import { applyPointOptionsColor, getTooltipContent, getNameOfCellConfig } from '../Izenda.CommonMapOptionsBuilder';

const ADJUST_LEGEND_X = 30;
const LENGTH_ALLOW_TWO = 2;
const FONT_SIZE_DEFAULT = 1.2;
/**
 * An implementation of ChartOptionsBuilder for Highmap.
 * This builder constructs the Highmap specific options, aka configuration.
 */
@exportClass('HighmapOptionsBuilder')
export default class HighmapOptionsBuilder extends ChartOptionsBuilder {
  constructor(...baseArgs) {
    super(...baseArgs);
  }

  build() {
    const { chartOptions, fieldOptions, chartContainer } = this;

    const mappedChartOptions = chartOptions;
    const mappedSeriesConfig = fieldOptions;

    const { commonActions, commonOptions, optionByType, colors } = mappedChartOptions;

    // add container to commonActions
    $izenda.extend(mappedChartOptions.commonActions, { chartContainer });

    // merge all optionByType into commonOptions
    $izenda.extend(true, commonOptions, optionByType);

    const izendaOptions = get('izendaOptions', commonOptions);
    const backgroundMap = get('backgroundMap', izendaOptions);
    const seriesMap = getOr({}, 'seriesMap', izendaOptions);
    const pointOption = getOr({}, 'pointOption', seriesMap);
    const printingMode = get('printingMode', izendaOptions);
    const activePointOption = get('activePointOption', izendaOptions);
    const pointType = get('pointType', activePointOption);

    // Create series data
    const bgSeries = getBackgroundMapSerie(
      mappedChartOptions,
      izendaOptions,
      pointOption,
      mappedSeriesConfig,
      seriesMap.backgroundMap
    );

    const { series, colorAxises } = getShadingMapSeriesAndColors(
      mappedChartOptions,
      izendaOptions,
      pointOption,
      pointType,
      mappedSeriesConfig,
      seriesMap.shadingSeries
    );

    const highmapSeries = [...bgSeries, ...series];

    // just init bubble series - do not draw
    // bubble series will be added and redawn later
    // to remove point which do not belong to current geo-map
    const bubbleSeries = getBubbleMapSeries(
      mappedChartOptions,
      izendaOptions,
      pointOption,
      mappedSeriesConfig,
      seriesMap.bubbleSeries,
      pointType,
      printingMode
    );
    // this is needed to put bubble series afterwards the shading series:
    const maxLegendPosition = max(map(x => x.legendIndex, highmapSeries)) || 0;
    bubbleSeries &&
      bubbleSeries.forEach(
        x => (x.legendIndex = maxLegendPosition + (!isNil(x.legendIndex) ? x.legendIndex : bubbleSeries.length))
      );

    // apply cell-color of point-options
    activePointOption && applyPointOptionsColor(highmapSeries, activePointOption, mappedSeriesConfig);
    activePointOption && applyPointOptionsColor(bubbleSeries, activePointOption, mappedSeriesConfig);

    // Generate Highmap options
    const highmapOptions = this.buildHighmapOptions(
      commonOptions,
      pointOption,
      highmapSeries,
      bubbleSeries,
      colorAxises,
      colors,
      seriesMap,
      backgroundMap,
      mappedSeriesConfig,
      commonActions
    );

    // Adjust legend position
    adjustLegendPosition(highmapOptions, seriesMap, chartContainer);

    return {
      bubbleSeries,
      activePointOption,
      seriesConfig: mappedSeriesConfig,
      ...highmapOptions
    };
  }

  /**
   * Build Highmap options
   */
  buildHighmapOptions(
    commonOptions,
    pointOption,
    highmapSeries,
    bubbleSeries,
    colorAxises,
    colors,
    seriesMap,
    backgroundMap,
    seriesConfig,
    commonActions
  ) {
    const onMapAreaClick = ({ options }) => {
      let point = options;
      if (isEmpty(seriesMap.shadingSeries) && !isEmpty(seriesMap.bubbleSeries)) {
        point = find({ code: point.code }, bubbleSeries[0].data) || point;
      }

      this.onDrilldown(point, commonActions);
    };

    const geojson = get('geojson', backgroundMap);

    // Map options
    const highmapOptions = $izenda.extend(true, commonOptions, {
      chart: { type: 'map' },
      title: {
        // do not show title
        text: ''
      },
      credits: { enabled: false },
      plotOptions: {
        map: {
          allAreas: false,
          point: {
            events: {
              click: function(e) {
                const $target = $izenda(e.target);
                if (!$target.hasClass('hyperlink-action')) onMapAreaClick(this);
                else return false;
              }
            }
          }
        },
        series: { mapData: geojson, clip: false }
      },
      tooltip: {
        formatter: function() {
          return showMapTooltip(this.point, pointOption, seriesConfig);
        },
        useHTML: true,
        backgroundColor: 'rgba(255,255,255,0)',
        borderWidth: 0,
        borderRadius: 0,
        shadow: false
      }
    });

    // set series to map-options
    highmapOptions.series = highmapSeries;
    highmapOptions.colorAxis = head(colorAxises);

    // apply theme colors for map
    !isNil(colors) && (highmapOptions.colors = colors);

    // omit all properties that have null or undefined value of highchart options.
    // It ensures the Highchart library doesn't throw error when processing null value.
    return omitBy(isNil, highmapOptions);
  }

  /**
   * Handle drilldown action when user click on the map
   * @param {Object} point Point value on the Map
   */
  onDrilldown(point, commonActions) {
    return commonActions.onDrilldown && commonActions.onDrilldown({ point });
  }
}
/**
 * Show map tooltip
 */
function showMapTooltip(point, pointOption, seriesConfig) {
  // background map: do not show tooltip
  if (get('series.userOptions.isBackgroundMap', point)) {
    return false;
  }

  const options = get('options', point);
  const series = get('series.userOptions', point);

  return getTooltipContent(options, pointOption, seriesConfig, series, point.color);
}

/**
 * Show data label
 * @param {boolean} isSupportedPointAction
 * @param {Object} activePointOption
 */
function showDataLabel(
  chartOptions: Object,
  izendaOptions: Object,
  pointOption: Object,
  seriesConfig: Object,
  isBackgroundMap: boolean,
  isSupportedPointAction: boolean,
  activePointOption: Object
) {
  const activePointOptionName = get('field.name', activePointOption);
  const dataLabelConfig = {
    enabled: !isBackgroundMap ? get('dataLabels', izendaOptions) : get('mapLabel', izendaOptions),
    padding: 0,
    rotation: 0,
    formatter: function() {
      const point = this.point;
      const pointOpts = point.options || {};
      const pointOptionLabel = get('label', pointOption);
      let pointName;
      const config = seriesConfig[pointOptionLabel];
      const userOptions = getOr({}, 'series.userOptions', point);
      const fieldSource = {
        element: pointOpts.nameField,
        record: pointOpts.record
      };

      if (!userOptions.isBackgroundMap && get('dataLabels', izendaOptions)) {
        pointName = getPointName.call(this);
        return pointName
          ? escape(getFormatteData(pointName, config, fieldSource))
          : buildPointNameByLongLat(seriesConfig, point);
      }

      if (izendaOptions.mapLabel) {
        pointName = getOr('', izendaOptions.dataLabelKey || 'name', pointOpts.pointInfo.properties);
        return escape(pointName ? getFormatteData(pointName, config, fieldSource) : undefined);
      }

      return undefined;
    }
  };

  const rotation = getOr(undefined, 'commonOptions.plotOptions.series.dataLabels.rotation', chartOptions);
  const result =
    isSupportedPointAction &&
    activePointOption &&
    addPointActionLink(
      true,
      chartOptions,
      dataLabelConfig,
      activePointOptionName,
      seriesConfig[activePointOptionName],
      getPointName,
      false,
      rotation
    );
  return result || dataLabelConfig;
}

/**
 * Get background map serie
 * @param {Object} series
 * @param {Object} backgroundMap
 */
function getBackgroundMapSerie(chartOptions, izendaOptions, pointOption, seriesConfig, backgroundMap: any) {
  if (!backgroundMap) {
    return [];
  }

  each(item => {
    item.color = MapUIConstant.backgroundMapColor;
  }, backgroundMap.data);

  return [
    {
      isBackgroundMap: true,
      data: backgroundMap.data,
      joinBy: backgroundMap.joinBy,
      color: MapUIConstant.backgroundMapColor,
      showInLegend: false,
      dataLabels: showDataLabel(chartOptions, izendaOptions, pointOption, seriesConfig, true, false),
      tooltip: {
        enabled: false
      }
    }
  ];
}

/**
 * Get bubble map series
 * @param {Object} seriesConfig
 * @param {Object} bubbleMap
 * @param {string} pointType
 * @param {boolean} printingMode
 */
function getBubbleMapSeries(
  chartOptions,
  izendaOptions,
  pointOption,
  seriesConfig: Object,
  bubbleMap: Object,
  pointType: string,
  printingMode: boolean
) {
  if (!bubbleMap) {
    return [];
  }

  return flatMap(serieKey => {
    const bubble = bubbleMap[serieKey];

    bubble.data.forEach(point => {
      point.z = !isNil(point.originalValue) && point.originalValue !== '' ? point.originalValue : point.value;
    });

    const childSeries = splitSeriesByColor(
      serieKey,
      bubble,
      get(['cellColors', pointType].join('.'), seriesConfig[serieKey])
    );

    return childSeries.map(serie =>
      $izenda.extend(
        {},
        bubble,
        {
          type: 'mapbubble',
          name: serieKey,
          showInLegend: true,
          dataLabels: showDataLabel(
            chartOptions,
            izendaOptions,
            pointOption,
            seriesConfig,
            false,
            true,
            bubble.activePointOption
          ),
          joinBy: ['hc-key', 'code'],
          minSize: MapUIConstant.bubbleMinSize,
          maxSize: MapUIConstant.bubbleMaxSize
        },
        serie
      )
    );
  }, Object.keys(bubbleMap));
}

function splitSeriesByColor(serieName, serie, cellColorConfig) {
  if (!cellColorConfig) {
    return [serie];
  }

  const rangeColors = cellColorConfig.rangePercent || cellColorConfig.rangeValue || [];
  const colorsInOriginalOrder = [...rangeColors.map(x => x.text), 'NO_COLOR'];

  const groupSeries = groupBy(point => {
    return (
      getConfigDataByRange(point.originalValue || point.value, cellColorConfig, {
        element: point.valueField,
        record: point.record
      }) || 'NO_COLOR'
    );
  }, serie.data);

  return Object.keys(groupSeries).map(color => {
    const childSerieName = getNameOfCellConfig(color, cellColorConfig);
    const legendIndex = colorsInOriginalOrder.indexOf(color);
    return {
      name: childSerieName || serieName,
      color: childSerieName ? color : null,
      hasColor: !!childSerieName, // flag: [TRUE] do not apply point-option color
      data: groupSeries[color],
      legendIndex: legendIndex >= 0 ? legendIndex : undefined
    };
  });
}

/**
 * Get shading map series and color axises
 * @param {Object} shadingSeries
 * @param {Object} axisColorSource
 */
function getShadingMapSeriesAndColors(
  chartOptions,
  izendaOptions,
  pointOption,
  pointType: string,
  seriesConfig: Object,
  shadingSeries: Object
) {
  if (!shadingSeries) {
    return [];
  }
  const colorAxises = [];

  const series = flatMap(serieKey => {
    const shading = shadingSeries[serieKey];

    shading.data.forEach(point => {
      point.z = !isNil(point.originalValue) && point.originalValue !== '' ? point.originalValue : point.value;
    });

    // only have 1 axis color
    const cellColors = get(['cellColors', pointType].join('.'), seriesConfig[serieKey]);
    const hasDynamicType = cellColors && some(x => x.thresholdType === 'dynamic', cellColors.value);
    const colorAxis = getColorAxis(serieKey, shading, cellColors);
    colorAxis && colorAxises.push(colorAxis);

    const childSeries = splitSeriesByColor(
      serieKey,
      shading,
      cellColors
    );

    return childSeries.map(serie =>
      $izenda.extend(
        {},
        shading,
        {
          data: shading.data,
          hasColor: colorAxis ? true : false,
          name: serieKey,
          joinBy: ['hc-key', 'code'], // TODO

          dataLabels: showDataLabel(
            chartOptions,
            izendaOptions,
            pointOption,
            seriesConfig,
            false,
            true,
            shading.activePointOption
          ),
          showInLegend: !hasDynamicType
        },
        serie
      )
    );
  }, Object.keys(shadingSeries));

  return { series, colorAxises };
}

/**
 * Get Color Axis
 * @param {String} serieName
 * @param {Object} serieInfo
 * @param {Object} cellColor
 * @return {Object} colorAxis
 */
function getColorAxis(serieName: string, serieInfo: any, cellColor: any) {
  const config = getOr(null, 'value', cellColor);
  if (!config || config.length !== LENGTH_ALLOW_TWO)
    return null;

  const minType = getOr(undefined, 'thresholdType', config[1]);
  const maxType = getOr(undefined, 'thresholdType', config[0]);
  const hasDynamicType = minType === 'dynamic' || maxType === 'dynamic';

  function getMin() {
    if (minType === 'static') return getNumber('key', config[1]);
    if (minType === 'dynamic_subtotal') return getOr(undefined, 'data[0].rangesObject.range[0][0]', serieInfo);
    return undefined;
  }

  function getMax() {
    if (maxType === 'static') return getNumber('key', config[0]);
    if (maxType === 'dynamic_subtotal') return getOr(undefined, 'data[0].rangesObject.range[0][1]', serieInfo);
    return undefined;
  }

  let min = getMin();
  let max = getMax();
   const range = max - min;
   if (range > 1) {
     min = Math.floor(min);
     max = Math.ceil(max);
   }
   return {
     minColor: getOr(undefined, 'text', config[1]),
     maxColor: getOr(undefined, 'text', config[0]),
     startOnTick: false,
     endOnTick: false,
     tickPositioner: getMapTicksPositioner(min, max),
     min,
     max,
     showInLegend: !hasDynamicType
   };
}

function getAxis(serieName, config, isRangePercent, firstPointVal, firstPointValPercent) {
  const getValue = (key, item) => {
    const val = getNumber(key, item);
    return isRangePercent ? (val * firstPointVal) / firstPointValPercent : val;
  };

  return size(config)
    ? {
        dataClasses: config.map(item => ({
          name: getOr(serieName, 'name', item),
          color: get('text', item),
          from: getValue('from', item),
          to: getValue('to', item)
        }))
      }
    : null;
}

/**
 * Adjust Legend Position
 * @param {Object} chartOptions
 * @param {Object} seriesMap
 * @param {Object} container
 */
function adjustLegendPosition(chartOptions: any, seriesMap: any, container: any) {
  const legend = getOr({}, 'legend', chartOptions);

  // adjust symbol size
  const fontSizeItem = parseInt(get('itemStyle.fontSize', legend));

  if (fontSizeItem) {
    const symbolSize = fontSizeItem * FONT_SIZE_DEFAULT;
    if (legend.layout === 'horizontal')
      legend.symbolHeight = symbolSize;
    else
      legend.symbolWidth = symbolSize;
  }

  // do not show legend no serie
  const sizeMapAllow = 1;
  if (size(seriesMap.bubbleSeries) + size(seriesMap.shadingSeries) < sizeMapAllow) {
    chartOptions.series.forEach(serie => {
      serie.isBackgroundMap && (serie.showInLegend = true);
    });
  }

  if (legend.enabled) {
    const containerWidth = container.clientWidth - ADJUST_LEGEND_X;
    const legendWidth =
      legend.layout === 'horizontal' && legend.verticalAlign != 'middle' ? containerWidth : containerWidth / 2;

    Object.assign(legend.title.style, {
      width: legendWidth,
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    });
  }
}

/**
 * Get formatted data
 * @param {string} val
 * @param {Object} config
 * @param {Object} fieldSource
 */
function getFormatteData(val: string, config: any, fieldSource: any) {
  return getOr('', 'fomattedValue', ChartDataParser.getValueStyle(val, (config: any), (fieldSource: any)));
}

/**
 * Get point name
 */
function getPointName() {
  return getOr('', 'options.pointName', this.point);
}

/**
 * Get number
 * @param {string} path
 * @param {Object} config
 */
function getNumber(path: string, config: any) {
  return flow(
    get(path),
    toNumber
  )(config);
}

/**
 * Build Point Name By Long Lat
 * @param {Object} point
 */
function buildPointNameByLongLat(seriesConfig, point: any) {
  if (!has('lat', point) || !has('lon', point)) {
    return '';
  }

  const series = getOr({}, 'series', point);
  const serieOpt = get('userOptions', series);
  const latConfig = get(get('fieldNameAlias', serieOpt.latField), seriesConfig);
  const lonConfig = get(get('fieldNameAlias', serieOpt.lonField), seriesConfig);

  const lat = ChartDataParser.getValueStyle(point.lat, latConfig, {
    element: serieOpt.latField,
    record: point.record
  });
  const lon = ChartDataParser.getValueStyle(point.lon, lonConfig, {
    element: serieOpt.lonField,
    record: point.record
  });

  return `(${get('fomattedValue', lat)}, ${get('fomattedValue', lon)})`;
}



// WEBPACK FOOTER //
// ./ui/share/components/charting/highcharts/Izenda.HighmapOptionsBuilder.js